import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Loader, Clock, X, Newspaper, Bookmark, TrendingUp, Radio, RefreshCw, ThumbsUp, ThumbsDown } from 'lucide-react';
import { fetchLocalNews } from './localNewsHandler';
import LocalNewsStation from './LocalNewsStation'; 
import { db } from '../../../firebase/firebase';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

// ============================================================================
// CONSTANTS
// ============================================================================

const COLOR_SCHEMES = [
  { color: '#2563ebff' },
  { color: '#16a34aff' },
  { color: '#9333eaff' },
  { color: '#ff7322ff' },
  { color: '#dc2626ff' }
];

const INITIAL_CARD_GRADIENT = {
  bg: 'linear-gradient(260deg, rgba(147, 197, 253, 1) 0%, rgba(96, 165, 250, 1) 53%, rgba(224, 242, 254, 0.97) 60%, rgba(191, 219, 254, 1) 60%, #dbeafeff 100%)',
  border: 'rgba(147, 197, 253, 0.5)',
  top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
  badge: '#2563ebff'
};

const SWIPE_THRESHOLD = 100;
const AUTO_ROTATE_DELAY = 5000;
const FRAME_THROTTLE = 16;

// ============================================================================
// SKELETON LOADING COMPONENT
// ============================================================================

const SkeletonSwipeCard = () => (
  <div style={{
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 2px)',
    maxWidth: '390px',
height: '360px',
    zIndex: 10
  }}>
    <div style={{
      width: '100%',
      height: '100%',
      background: 'rgba(255, 255, 255, 0.31)',
      backdropFilter: 'blur(20px)',
      borderRadius: '18px',
      padding: '4px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.93)'
    }}>
      <div style={{
        width: '100%',
        height: '100%',
        background: 'white',
        borderRadius: '15px',
        border: '1px solid rgba(213, 213, 213, 0.5)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ 
          width: '100%', 
          height: '180px',
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite'
        }} />
        
        <div style={{ padding: '14px', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ 
            height: '16px', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            width: '90%'
          }} />
          <div style={{ 
            height: '16px', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            width: '70%'
          }} />
          
          <div style={{ 
            height: '12px', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            width: '95%',
            marginTop: '4px'
          }} />
          <div style={{ 
            height: '12px', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            width: '60%'
          }} />
          
          <div style={{ 
            marginTop: 'auto',
            height: '36px', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '18px'
          }} />
        </div>
      </div>
    </div>
  </div>
);

// ============================================================================
// INITIAL STATE COMPONENT
// ============================================================================

const InitialCard = ({ isGenerating, error, onFetchNews }) => (
  
  <div style={{
    marginLeft: '-16px',
    marginRight: '-16px',
    marginBottom: '40px'
  }}>
    <div style={{
      background: INITIAL_CARD_GRADIENT.bg,
      borderRadius: '16px',
      margin: '0 16px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: `1px solid ${INITIAL_CARD_GRADIENT.border}`,
      overflow: 'hidden',
      position: 'relative'
    }}>
      <div style={{
        background: INITIAL_CARD_GRADIENT.top,
        padding: '16px 16px 12px 16px',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          background: INITIAL_CARD_GRADIENT.badge,
          color: 'white',
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '9px',
          fontWeight: '500',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <Newspaper size={10} />
          Local News
        </div>

        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '5px 20px',
          display: 'inline-block',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginTop: '45px',
          marginBottom: '25px',
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '500',
            color: '#1f2937',
            textAlign: 'left'
          }}>
         Find local news in your area
          </div>
        </div>
      </div>

      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '0 0 14px 14px'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#9ca3af',
            marginBottom: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Description:
          </div>
                            <div className="item-meta"  style={{
    background: "transparent",
    borderRadius: 0,
    padding: "10px 14px",
    marginBottom: "0px",
    border: "none",
    borderTop: "1px solid #d9e0ecff",
  }}>
                  </div>
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            lineHeight: '1.5',
            marginBottom: '28px',
          }}>
Stay informed with local news with 24/7 personalized podcast about what’s happening around you and nearby.        </div>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '12px',
            fontSize: '11px',
            color: '#991b1b'
          }}>
            {error}
          </div>
        )}

<button
  onClick={onFetchNews}
  disabled={isGenerating}
  style={{
    width: '32%',
    background: isGenerating ? '#94a3b8' : '#000000',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    padding: '7px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: isGenerating ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    margin: '0 auto'
  }}
>
  {isGenerating ? (
    <>
      <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
      <span>Searching...</span>
    </>
  ) : (
    <>
      <span>Try It</span>
    </>
  )}
</button>

      </div>
    </div>
  </div>
);

// ============================================================================
// NEWS CARD IMAGE SECTION
// ============================================================================

const NewsCardImage = ({ 
  story, 
  source, 
  isCurrentCard, 
  savedArticles,    
  onToggleSave,     
  isArticleSaved,   
  onRemoveArticle,
  onSaveArticle     // ✅ ADD THIS
}) => (
  <div style={{ 
    position: 'relative', 
    width: '100%', 
    height: '180px',
    flexShrink: 0
  }}>
    <img 
      src={story.image} 
      alt={story.title}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        pointerEvents: 'none',
        userSelect: 'none'
      }}
      draggable="false"
    />
    
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '50px',
      background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)'
    }} />

    {/* Top Bar - Location & Bookmark */}
    <div style={{
      position: 'absolute',
      top: '10px',
      left: '10px',
      right: '10px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '6px 12px',
        borderRadius: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }}>
        <MapPin size={14} color={source.color} />
        <span style={{
          fontSize: '11px',
          fontWeight: '600',
          color: '#1f2937'
        }}>
          {source.name}
        </span>
      </div>

      {isCurrentCard && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            
            if (isArticleSaved(story.source)) {
              // ✅ If saved, find the saved article's Firestore ID and remove it
              const savedArticle = savedArticles?.find(a => a.source === story.source);
              if (savedArticle?.id) {
                onRemoveArticle(savedArticle.id);
              }
            } else {
              // ✅ Save the article with source metadata
              onSaveArticle({
                title: story.title,
                description: story.description,
                image: story.image,
                source: story.source,
                time: story.time,
                sourceName: source.name,
                sourceLogo: null,
                sourceColor: source.color,
                type: 'local-news' // ✅ Mark as local news
              });
            }
          }}
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: isArticleSaved(story.source)
              ? 'rgba(254, 243, 199, 0.95)'
              : 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(10px)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s'
          }}
        >
          <Bookmark
            size={14}
            color={isArticleSaved(story.source) ? '#92400e' : '#ffffffff'}
            fill={isArticleSaved(story.source) ? '#92400e' : 'none'}
          />
        </button>
      )}
    </div>
  </div>
);
// ============================================================================
// NEWS CARD CONTENT SECTION
// ============================================================================


const NewsCardContent = ({ 
  story, 
  source, 
  storyIdx, 
  isCurrentCard, 
  onStorySelect, 
  onReadMore,
  onOpenStation
}) => (
  <div style={{
    height: '194px',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  }}>
    {/* Title */}
    <h3 style={{
      fontSize: '13px',
      fontWeight: '600',
      color: '#1f2937',
      lineHeight: '1.3',
      margin: 0,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textAlign: 'left'
    }}>
      {story.title}
    </h3>

    {/* Description only - no custom time */}
    <p style={{
      fontSize: '10px',
      color: '#6b7280',
      lineHeight: '1.4',
      margin: 0,
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textAlign: 'left'
    }}>
      {story.description}
    </p>

    {isCurrentCard && (
      <>
        {/* Story Progress Dots */}
        <div style={{
          display: 'flex',
          gap: '4px',
          justifyContent: 'center',
          marginTop: '6px'
        }}>
          {source.stories.map((_, idx) => (
            <div
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                onStorySelect(idx);
              }}
              style={{
                width: idx === storyIdx ? '16px' : '5px',
                height: '5px',
                borderRadius: '3px',
                background: idx === storyIdx ? source.color : '#d1d5db',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
            />
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{
          marginTop: 'auto',
          display: 'flex',
          gap: '8px'
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReadMore(story.source);
            }}
            style={{
              flex: 1,
              padding: '10px',
              background: '#f3f4f6',
              color: '#1f2937',
              border: 'none',
              borderRadius: '18px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Read Full Story
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenStation();
            }}
            style={{
              flex: 1,
              padding: '10px',
              background: '#000000',
              color: 'white',
              border: 'none',
              borderRadius: '18px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}
          >
            <Radio size={14} />
            Tune In
          </button>
        </div>
      </>
    )}
  </div>
);
// ============================================================================
// SINGLE NEWS CARD COMPONENT
// ============================================================================
const NewsCard = ({ 
  source, 
  storyIdx, 
  isCurrentCard, 
  stackPosition,
  visualDragOffset,
  isDraggingVisual,
  swipeProgress,
  opacity,
  rotation,
  savedArticles,
  onStorySelect,
  onMouseDown,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onOpenStation,
  isArticleSaved,
  onSaveArticle,
  onRemoveArticle
}) => {
  if (!source || !source.stories || source.stories.length === 0) return null;

  const story = source.stories[storyIdx];
  if (!story) return null;

  // Calculate card positioning
  const isNextCard = stackPosition === 1;
  let zIndex, scale, translateY, cardOpacity;
  
  if (isCurrentCard) {
    zIndex = 10;
    scale = 1 - (swipeProgress * 0.05);
    translateY = 0;
    cardOpacity = opacity;
  } else if (isNextCard) {
    zIndex = 9;
    const baseScale = 0.95;
    const targetScale = 1;
    scale = baseScale + ((targetScale - baseScale) * swipeProgress);
    
    const baseY = 8;
    const targetY = 0;
    translateY = baseY - (baseY * swipeProgress);
    
    const baseOpacity = 0.7;
    const targetOpacity = 1;
    cardOpacity = baseOpacity + ((targetOpacity - baseOpacity) * swipeProgress);
  } else {
    zIndex = 9 - stackPosition;
    scale = 0.90 - ((stackPosition - 1) * 0.05);
    translateY = 8 + ((stackPosition - 1) * 8);
    cardOpacity = 0.5;
  }

  // Calculate transform
  let transform;
  if (isCurrentCard && isDraggingVisual) {
    transform = `translate3d(calc(-50% + ${visualDragOffset}px), ${translateY}px, 0) rotate(${rotation}deg) scale(${scale})`;
  } else {
    transform = `translate3d(-50%, ${translateY}px, 0) scale(${scale})`;
  }

  const hasTransition = !isDraggingVisual;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: '50%',
        transform,
        width: 'calc(100% - 2px)',
        maxWidth: '390px',
height: '370px',
        zIndex,
        opacity: cardOpacity,
        transition: hasTransition ? 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease-out' : 'none',
        cursor: isCurrentCard ? (isDraggingVisual ? 'grabbing' : 'grab') : 'default',
        pointerEvents: isCurrentCard ? 'auto' : 'none',
        willChange: 'transform, opacity',
        WebkitTransform: transform,
        backfaceVisibility: 'hidden',
        perspective: 1000,
        WebkitPerspective: 1000,
        transformStyle: 'preserve-3d'
      }}
      onMouseDown={isCurrentCard ? onMouseDown : undefined}
      onTouchStart={isCurrentCard ? onTouchStart : undefined}
      onTouchMove={isCurrentCard ? onTouchMove : undefined}
      onTouchEnd={isCurrentCard ? onTouchEnd : undefined}
    >
      <div style={{
        width: '100%',
        height: '100%',
        background: 'rgba(255, 255, 255, 0.31)',
        backdropFilter: 'blur(20px)',
        borderRadius: '18px',
        padding: '4px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.93)'
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          background: 'white',
          borderRadius: '15px',
          border: '1px solid rgba(213, 213, 213, 0.5)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
    <NewsCardImage
      story={story}
      source={source}
      isCurrentCard={isCurrentCard}
      savedArticles={savedArticles}
      isArticleSaved={isArticleSaved}
      onSaveArticle={onSaveArticle}
      onRemoveArticle={onRemoveArticle}
    />

          {/* Story count overlay for image bottom bar */}
          <div style={{
            position: 'absolute',
top: '156px',
            right: '14px',
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(10px)',
            padding: '4px 9px',
            borderRadius: '14px',
            fontSize: '9px',
            fontWeight: '600',
            color: 'white',
            pointerEvents: 'none'
          }}>
            {storyIdx + 1}/{source.stories.length}
          </div>

<NewsCardContent
  story={story}
  source={source}
  storyIdx={storyIdx}
  isCurrentCard={isCurrentCard}
  onStorySelect={onStorySelect}
  onReadMore={(url) => window.open(url, '_blank')}
  onOpenStation={onOpenStation}
/>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HEADER COMPONENT
// ============================================================================

const NewsHeader = ({ userLocation, onClose }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  }}>
    <div>
    <h2 style={{ 
      fontSize: '12.5px', 
      fontWeight: '600', 
      color: '#1f2937', 
      margin: '0 0 16px 0', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px' ,
      marginBottom: '10px'
    }}>
    <Newspaper size={13} color='#338bfdff'/>
        Local News
    </h2>
    
      <p style={{
        fontSize: '11px',
        color: '#6b7280',
        margin: 0
      }}>
        {userLocation.city}{userLocation.state ? `, ${userLocation.state}` : ''}
      </p>
    </div>

    <button
      onClick={onClose}
      style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
    >
      <X size={16} color="#6b7280" />
    </button>
  </div>
);

// ============================================================================
// SOURCE NAVIGATION DOTS
// ============================================================================

const SourceNavigationDots = ({ sources, currentIndex, onNavigate, onOpenStation }) => (
  <div style={{
    position: 'absolute',
bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    zIndex: 20
  }}>
    {/* Navigation Dots */}
    <div style={{
      display: 'flex',
      gap: '5px',
      alignItems: 'center',
    }}>
      {sources.map((source, idx) => (
        <button
          key={source.id}
          onClick={() => onNavigate(idx)}
          title={source.name}
          style={{
            width: idx === currentIndex ? '22px' : '4px',
            height: '5px',
            borderRadius: '4px',
            background: idx === currentIndex ? '#272727' : '#b1b5b9ff',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            padding: 0
          }}
        />
      ))}
    </div>
  </div>
);
// ============================================================================
// CUSTOM HOOKS
// ============================================================================

// Hook for drag gesture handling
const useDragGesture = (newsSources, onSwipeComplete) => {
  const dragStateRef = useRef({
    isDragging: false,
    dragStart: null,
    dragStartY: null,
    dragOffset: 0
  });
  
  const [visualDragOffset, setVisualDragOffset] = useState(0);
  const [isDraggingVisual, setIsDraggingVisual] = useState(false);
  const animationFrameRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);

  const updateVisualState = useCallback(() => {
    const now = performance.now();
    if (now - lastUpdateTimeRef.current < FRAME_THROTTLE) return;
    
    lastUpdateTimeRef.current = now;
    setVisualDragOffset(dragStateRef.current.dragOffset);
    setIsDraggingVisual(dragStateRef.current.isDragging);
  }, []);

  const resetDragState = useCallback(() => {
    dragStateRef.current = {
      isDragging: false,
      dragStart: null,
      dragStartY: null,
      dragOffset: 0
    };
    setVisualDragOffset(0);
    setIsDraggingVisual(false);
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0 || newsSources.length <= 1) return;
    dragStateRef.current.dragStart = e.clientX;
    dragStateRef.current.dragStartY = e.clientY;
    dragStateRef.current.isDragging = true;
    setIsDraggingVisual(true);
  }, [newsSources.length]);

  const handleMouseMove = useCallback((e) => {
    if (!dragStateRef.current.isDragging) return;
    
    const deltaX = e.clientX - dragStateRef.current.dragStart;
    const deltaY = Math.abs(e.clientY - dragStateRef.current.dragStartY);
    
    if (Math.abs(deltaX) > deltaY * 1.5) {
      dragStateRef.current.dragOffset = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(updateVisualState);
    }
  }, [updateVisualState]);

  const handleMouseUp = useCallback(() => {
    if (!dragStateRef.current.isDragging) return;
    
    if (Math.abs(dragStateRef.current.dragOffset) > SWIPE_THRESHOLD) {
      onSwipeComplete();
      resetDragState();
    } else {
      resetDragState();
    }
  }, [onSwipeComplete, resetDragState]);

  const handleTouchStart = useCallback((e) => {
    if (newsSources.length <= 1) return;
    dragStateRef.current.dragStart = e.touches[0].clientX;
    dragStateRef.current.dragStartY = e.touches[0].clientY;
    dragStateRef.current.isDragging = true;
    setIsDraggingVisual(true);
  }, [newsSources.length]);

  const handleTouchMove = useCallback((e) => {
    if (!dragStateRef.current.isDragging) return;
    
    const deltaX = e.touches[0].clientX - dragStateRef.current.dragStart;
    const deltaY = Math.abs(e.touches[0].clientY - dragStateRef.current.dragStartY);
    
    if (Math.abs(deltaX) > deltaY * 1.5) {
      dragStateRef.current.dragOffset = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(updateVisualState);
    }
  }, [updateVisualState]);

  const handleTouchEnd = useCallback(() => {
    if (!dragStateRef.current.isDragging) return;
    
    if (Math.abs(dragStateRef.current.dragOffset) > SWIPE_THRESHOLD) {
      onSwipeComplete();
      resetDragState();
    } else {
      resetDragState();
    }
  }, [onSwipeComplete, resetDragState]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Global mouse listeners
  useEffect(() => {
    if (isDraggingVisual) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingVisual, handleMouseMove, handleMouseUp]);

  return {
    visualDragOffset,
    isDraggingVisual,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  };
};
// Hook for news data fetching
const useNewsData = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [newsSources, setNewsSources] = useState([]);
  const [error, setError] = useState(null);
const [lastFetchedLocation, setLastFetchedLocation] = useState(null);

// Get current user location via Geolocation API
const getCurrentUserLocation = async () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not available on this device'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        console.error('❌ Geolocation error:', error.code, error.message);
        
        if (error.code === 1) {
          reject(new Error('Permission denied - enable location access'));
        } else if (error.code === 2) {
          reject(new Error('Position unavailable - try again'));
        } else if (error.code === 3) {
          reject(new Error('Location request timed out'));
        } else {
          reject(new Error('Could not get location'));
        }
      },
      { 
        timeout: 10000, 
        enableHighAccuracy: true,
        maximumAge: 0 // Don't use cached location
      }
    );
  });
};

// Check if location has changed (using distance calculation)
const hasLocationChanged = async (lastLocation) => {
  try {
    const currentLocation = await getCurrentUserLocation();
    
    if (!lastLocation) return true;
    
    // ✅ Haversine formula for accurate distance
    const R = 6371; // Earth's radius in km
    const lat1 = lastLocation.latitude * Math.PI / 180;
    const lat2 = currentLocation.latitude * Math.PI / 180;
    const deltaLat = (currentLocation.latitude - lastLocation.latitude) * Math.PI / 180;
    const deltaLon = (currentLocation.longitude - lastLocation.longitude) * Math.PI / 180;
    
    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    
    console.log(`📍 Distance from last location: ${distance.toFixed(2)} km`);
    
    // If more than 5km away, consider it changed
    return distance > 5;
  } catch (error) {
    console.error('❌ Error checking location:', error);
    return false;
  }
};

const fetchNews = async (isRefresh = false) => {
  try {
    setIsGenerating(true);
    setError(null);

    // Location check for refresh
    let locationStatusMessage = null;

    if (isRefresh && lastFetchedLocation) {
      console.log('═══════════════════════════════════════');
      console.log('📍 LOCATION CHECK STARTED');
      console.log('═══════════════════════════════════════');
      console.log('📌 Last Location:', lastFetchedLocation);
      
      try {
        const locationChanged = await hasLocationChanged(lastFetchedLocation);
        console.log('📊 LOCATION CHECK RESULT:', locationChanged ? '✅ CHANGED' : '❌ SAME');
        
        if (locationChanged) {
          console.log('🚀 Getting news for NEW location...');
          locationStatusMessage = '📍 Location changed! Getting new local news...';
        } else {
          console.log('🔄 Refreshing news for SAME location...');
          locationStatusMessage = '📍 Location same - refreshing latest news...';
        }
      } catch (error) {
        console.error('❌ Location check error:', error);
      }
    }

    console.log('🔍 Fetching local news...');
    const newsData = await fetchLocalNews(true);
    console.log('✅ News data received:', newsData);

    // Validate newsData
    if (!newsData) {
      throw new Error('No data received from news service');
    }

    if (newsData.totalArticles === 0) {
      console.warn('⚠️ Backend returned 0 articles');
      throw new Error('No news articles found for your area. Please try again.');
    }

    if (!newsData.mainCity || !newsData.mainCity.name) {
      throw new Error('Invalid news data structure');
    }

    const locationData = {
      city: newsData.mainCity.name,
      state: newsData.mainCity.state,
      country: newsData.mainCity.country
    };
    
    setUserLocation(locationData);

    const sources = [];
    
    // ============================================
    // MAIN CITY NEWS
    // ============================================
    if (newsData.mainCity.articles && newsData.mainCity.articles.length > 0) {
      console.log(`📰 Adding main city: ${newsData.mainCity.name} (${newsData.mainCity.articles.length} articles)`);
      
      const mainCityArticles = newsData.mainCity.articles.slice(0, 5).map(article => ({
        title: article.title,
        description: article.snippet,
        image: article.imageUrl || '/image/studycard7.png',
        time: article.timeAgo,
        source: article.url
      }));
      
      sources.push({
        id: 'main-city',
        name: newsData.mainCity.name,
        logo: null,
        color: COLOR_SCHEMES[0].color,
        stories: mainCityArticles
      });
    }

    // ============================================
    // NEARBY CITIES NEWS - FIXED
    // ============================================
    if (newsData.nearbyCities && Array.isArray(newsData.nearbyCities)) {
      console.log(`📍 Processing ${newsData.nearbyCities.length} nearby cities...`);
      
      newsData.nearbyCities.forEach((city, idx) => {
        console.log(`   🏙️ Nearby city ${idx + 1}: ${city.name} - ${city.articles?.length || 0} articles`);
        
        // ✅ FIX: Check if city has articles AND they're not empty
        if (city.articles && Array.isArray(city.articles) && city.articles.length > 0) {
          const cityArticles = city.articles.slice(0, 3).map(article => ({
            title: article.title,
            description: article.snippet,
            image: article.imageUrl || '/image/studycard6.png',
            time: article.timeAgo,
            source: article.url
          }));
          
          sources.push({
            id: `nearby-${idx}`,
            name: city.name,
            logo: null,
            color: COLOR_SCHEMES[(idx + 1) % COLOR_SCHEMES.length].color,
            stories: cityArticles
          });
          
          console.log(`   ✅ Added ${city.name} to sources`);
        } else {
          console.log(`   ⚠️ Skipped ${city.name} (no articles)`);
        }
      });
    } else {
      console.log('📍 No nearby cities data available');
    }

    // ============================================
    // STATE/REGIONAL NEWS
    // ============================================
    if (newsData.stateNews && newsData.stateNews.articles && newsData.stateNews.articles.length > 0) {
      console.log(`🏛️ Adding state news: ${newsData.stateNews.name} (${newsData.stateNews.articles.length} articles)`);
      
      const stateArticles = newsData.stateNews.articles.slice(0, 3).map(article => ({
        title: article.title,
        description: article.snippet,
        image: article.imageUrl || '/image/studycard2.png',
        time: article.timeAgo,
        source: article.url
      }));
      
      sources.push({
        id: 'state',
        name: newsData.stateNews.name,
        logo: null,
        color: COLOR_SCHEMES[4].color,
        stories: stateArticles
      });
    }

    // ============================================
    // VALIDATE SOURCES
    // ============================================
    if (sources.length === 0) {
      console.error('❌ No sources created from newsData');
      throw new Error('No news articles available for your area. Please try again.');
    }

    console.log(`✅ Created ${sources.length} news sources:`);
    sources.forEach((source, idx) => {
      console.log(`   ${idx + 1}. ${source.name} (${source.stories.length} stories)`);
    });
    
    setNewsSources(sources);

    // Save location coordinates
    setLastFetchedLocation({
      latitude: newsData.mainCity.coords?.latitude || 0,
      longitude: newsData.mainCity.coords?.longitude || 0,
      city: newsData.mainCity.name,
      state: newsData.mainCity.state
    });

    setHasGenerated(true);

    // Save to Firebase
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user) {
      try {
        const newsDocRef = doc(db, 'users', user.uid, 'localNews', 'current');
        await setDoc(newsDocRef, {
          sources: sources,
          location: locationData,
          lastFetchedCoordinates: {
            latitude: newsData.mainCity.coords?.latitude || 0,
            longitude: newsData.mainCity.coords?.longitude || 0
          },
          savedAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
        console.log('✅ News saved to Firebase');
      } catch (fbError) {
        console.error('❌ Failed to save news to Firebase:', fbError);
      }
    }

  } catch (err) {
    console.error('❌ Error in fetchNews:', err);
    
    let errorMessage = err.message || 'Could not load local news. Please try again.';
    
    if (err.message.includes('Location permission')) {
      errorMessage = 'Please enable location access to see local news.';
    } else if (err.message.includes('timeout')) {
      errorMessage = 'Request timed out. Please check your connection.';
    } else if (err.message.includes('No news articles')) {
      errorMessage = 'No local news available right now. Please try again in a few moments.';
    }
    
    setError(errorMessage);
  } finally {
    setIsGenerating(false);
  }
};
  const reset = useCallback(() => {
    setHasGenerated(false);
    setNewsSources([]);
    setError(null);
    setUserLocation(null);
  }, []);

  // ✅ NEW: Function to load saved data from Firebase
  const loadFromFirebase = useCallback(async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) return false;
    
    try {
      const newsDocRef = doc(db, 'users', user.uid, 'localNews', 'current');
      const newsDoc = await getDoc(newsDocRef);
      
      if (newsDoc.exists()) {
        const data = newsDoc.data();
        
        if (data.sources && data.sources.length > 0 && data.location) {
          setNewsSources(data.sources);
          setUserLocation(data.location);
            // ✅ NEW: Restore last fetched coordinates
  if (data.lastFetchedCoordinates) {
    setLastFetchedLocation({
      latitude: data.lastFetchedCoordinates.latitude,
      longitude: data.lastFetchedCoordinates.longitude,
      city: data.location.city,
      state: data.location.state
    });
  }
  
          setHasGenerated(true);
          console.log('✅ Loaded saved news from Firebase');
          return true;
        }
      }
    } catch (error) {
      console.error('❌ Failed to load news from Firebase:', error);
    }
    
    return false;
  }, []);

  return {
    isGenerating,
    hasGenerated,
    userLocation,
    newsSources,
    error,
    fetchNews,
    reset,
    loadFromFirebase, // ✅ Expose this function
      hasLocationChanged,        // ✅ NEW
  getCurrentUserLocation,    // ✅ NEW
  lastFetchedLocation        // ✅ NEW
  };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// ============================================================================
// COMPLETE LocalNewsCard COMPONENT WITH FIREBASE + SKELETON
// ============================================================================

const LocalNewsCard = ({ 
  onSaveArticle,
  isArticleSaved,
  onRemoveArticle,
  savedArticles
}) => {
  // Data state
const {
  isGenerating,
  hasGenerated,
  userLocation,
  newsSources,
  error,
  fetchNews,
  reset,
  loadFromFirebase,
  hasLocationChanged,        // ✅ Should be here
  getCurrentUserLocation,    // ✅ Should be here
  lastFetchedLocation        // ✅ Should be here
} = useNewsData();

  // Navigation state
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [showStation, setShowStation] = useState(false);
  const [locationChangeMessage, setLocationChangeMessage] = useState(null);
  const [thumbsState, setThumbsState] = useState({}); // Track thumbs per card


  // User and Firebase states
  const [userName, setUserName] = useState('User');
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingFromFirebase, setIsLoadingFromFirebase] = useState(true);
  const [hasCheckedFirebase, setHasCheckedFirebase] = useState(false); // ✅ NEW: Track if we checked
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
const [isInitialMount, setIsInitialMount] = useState(true); // ✅ NEW: Track first mount


  // ============================================================================
  // FETCH USER NAME FROM FIREBASE
  // ============================================================================
  
  
 
  // ============================================================================
  // FETCH USER NAME FROM FIREBASE
  // ============================================================================
// ============================================================================
// FETCH USER NAME FROM FIREBASE
// ============================================================================

useEffect(() => {
  const auth = getAuth();
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const name = userData.settings?.userName || userData.displayName || 'User';
          setUserName(name);
        }
      } catch (error) {
        console.error('Error fetching user name:', error);
      } finally {
        setIsLoadingUser(false);
      }
    } else {
      setIsLoadingUser(false);
    }
  });

  return () => unsubscribe();
}, []);

// ============================================================================
// ✅ LOAD SAVED NEWS ON MOUNT (ONLY IF EXISTS)
// ============================================================================

useEffect(() => {
  const loadSavedNews = async () => {
    if (isLoadingUser) return;
    
    if (!hasCheckedFirebase) {
      setHasCheckedFirebase(true);
      
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        // ✅ Show skeleton for 1 seconds even if no user
        await new Promise(resolve => setTimeout(resolve, 200));
        setIsLoadingFromFirebase(false);
        setIsInitialMount(false);
        return;
      }
      
      try {
        const newsDocRef = doc(db, 'users', user.uid, 'localNews', 'current');
        const newsDoc = await getDoc(newsDocRef);
        
        if (newsDoc.exists()) {
          const data = newsDoc.data();
          
          if (data.sources && data.sources.length > 0 && data.location) {
            console.log('✅ Found saved news, showing skeleton...');
            
            // ✅ Show skeleton for 2 seconds
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const loaded = await loadFromFirebase();
            
            if (loaded) {
              console.log('✅ Successfully loaded saved news');
            }
          } else {
            console.log('📭 No valid saved news found');
            // ✅ Show skeleton for 2 seconds even if no data
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } else {
          console.log('📭 No saved news document found');
          // ✅ Show skeleton for 2 seconds even if no document
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error('❌ Failed to check for saved news:', error);
        // ✅ Show skeleton for 2 seconds even on error
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      setIsLoadingFromFirebase(false);
      setIsInitialMount(false);
    }
  };
  
  loadSavedNews();
}, [isLoadingUser, hasCheckedFirebase, loadFromFirebase]);
// ============================================================================
// CLOSE CONFIRMATION HANDLERS
// ============================================================================

const handleCloseClick = () => {
  setShowCloseConfirm(true);
};

const confirmClose = async () => {
  setShowCloseConfirm(false);
  
  // Clear Firebase data
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (user) {
    try {
      const newsDocRef = doc(db, 'users', user.uid, 'localNews', 'current');
      await setDoc(newsDocRef, {
        sources: [],
        location: null,
        clearedAt: serverTimestamp()
      });
      console.log('✅ News cleared from Firebase');
    } catch (error) {
      console.error('❌ Failed to clear news:', error);
    }
  }
  
  reset();
  setHasCheckedFirebase(false); // ✅ Allow checking again after reset
};

const cancelClose = () => {
  setShowCloseConfirm(false);
};

// ============================================================================
// REFRESH HANDLER
// ============================================================================

const handleRefresh = async () => {
  setCurrentSourceIndex(0);
  setCurrentStoryIndex(0);
  
  const shouldCheckLocation = lastFetchedLocation !== null;
  
  // ✅ ADD THIS for console visibility
  console.log('🔄 REFRESH CLICKED');
  console.log('📍 Last Fetched Location:', lastFetchedLocation);
  console.log('🔍 Should check location:', shouldCheckLocation);
  
  await fetchNews(shouldCheckLocation);
  
  // ✅ Show location message after refresh
  if (shouldCheckLocation) {
    setLocationChangeMessage('📍 Location checked - News refreshed!');
    setTimeout(() => setLocationChangeMessage(null), 3000);
  }
};
  // ============================================================================
  // SAVE NEWS TO FIREBASE
  // ============================================================================
  // ============================================================================
  // LOAD NEWS FROM FIREBASE
  // ============================================================================


  // ============================================================================
  // REFRESH HANDLER
  // ============================================================================

  // Navigation functions
  const goToNextSource = useCallback(() => {
    setCurrentSourceIndex((prev) => (prev + 1) % newsSources.length);
    setCurrentStoryIndex(0);
  }, [newsSources.length]);

  // Drag gesture handling
  const {
    visualDragOffset,
    isDraggingVisual,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  } = useDragGesture(newsSources, goToNextSource);



  // Auto-rotate stories
  useEffect(() => {
    if (!hasGenerated || newsSources.length === 0) return;
    
    const currentSource = newsSources[currentSourceIndex];
    if (!currentSource) return;
    
    const interval = setInterval(() => {
      if (currentStoryIndex < currentSource.stories.length - 1) {
        setCurrentStoryIndex(prev => prev + 1);
      } else {
        goToNextSource();
      }
    }, AUTO_ROTATE_DELAY);
    
    return () => clearInterval(interval);
  }, [currentSourceIndex, currentStoryIndex, hasGenerated, newsSources, goToNextSource]);

  // Calculate swipe effects
  const rotation = visualDragOffset * 0.05;
  const opacity = isDraggingVisual ? Math.max(0.7, 1 - Math.abs(visualDragOffset) / 300) : 1;
  const swipeProgress = Math.min(Math.abs(visualDragOffset) / 150, 1);

if (!hasGenerated && !isLoadingFromFirebase) {
  return (
    <InitialCard
      isGenerating={isGenerating}
      error={error}
      onFetchNews={fetchNews}
    />
  );
}
  // ============================================================================
  // RENDER: LOADING FROM FIREBASE (SKELETON)
  // ============================================================================
if (isLoadingFromFirebase) {
  return (
    <div style={{ marginBottom: '38px' }}>
      {/* Header Skeleton */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            width: '120px',
            height: '14px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            marginBottom: '8px'
          }} />
          <div style={{
            width: '180px',
            height: '11px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px'
          }} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }} />
        </div>
      </div>

      {/* Card Skeleton */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '400px',
        perspective: '1000px'
      }}>
        <SkeletonSwipeCard />
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}


  // ============================================================================
  // RENDER: LOADING STATE
  // ============================================================================
  

if (isGenerating) {
  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '360px',
      perspective: '1000px',
      marginBottom: '20px'
    }}>
      <SkeletonSwipeCard />
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

  // Validate data
  const currentSource = newsSources[currentSourceIndex];
  if (!currentSource || !currentSource.stories || currentSource.stories.length === 0) {
    return null;
  }

  // ============================================================================
  // RENDER: NEWS CARDS WITH HEADER
  // ============================================================================
  
  return (
    <div style={{ marginBottom: '38px' }}>
      {/* HEADER WITH REFRESH + CLOSE */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div>
          <h2 style={{ 
            fontSize: '12.5px', 
            fontWeight: '600', 
            color: '#1f2937', 
            margin: '0 0 16px 0', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '10px'
          }}>
            <Newspaper size={13} color='#338bfdff'/>
            Local News
          </h2>
          
          <p style={{
            fontSize: '11px',
            color: '#6b7280',
            margin: 0
          }}>
            {userLocation.city}{userLocation.state ? `, ${userLocation.state}` : ''}
          </p>
        </div>

        {/* RIGHT SIDE BUTTONS: REFRESH + CLOSE */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* REFRESH BUTTON */}
          <button
            onClick={handleRefresh}
            disabled={isGenerating}
            title="Refresh news"
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: isGenerating ? 0.5 : 1
            }}
          >
            <RefreshCw 
              size={16} 
              color="#6b7280" 
              style={{
                animation: isGenerating ? 'spin 1s linear infinite' : 'none'
              }} 
            />
          </button>

          {/* CLOSE BUTTON */}
          <button
            onClick={handleCloseClick}
            title="Close"
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <X size={16} color="#6b7280" />
          </button>
        </div>
      </div>

      {/* Card Stack Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '400px',
        perspective: '1000px'
      }}>
        {/* Render card stack - back to front */}
        {[2, 1, 0].map((offset) => {
          const sourceIndex = (currentSourceIndex + offset) % newsSources.length;
          const source = newsSources[sourceIndex];
          const isCurrentCard = offset === 0;
          const storyIdx = isCurrentCard ? currentStoryIndex : 0;

          return (
<NewsCard
  key={`${source.id}-${offset}`}
  source={source}
  storyIdx={storyIdx}
  isCurrentCard={isCurrentCard}
  stackPosition={offset}
  visualDragOffset={visualDragOffset}
  isDraggingVisual={isDraggingVisual}
  swipeProgress={swipeProgress}
  opacity={opacity}
  rotation={rotation}
  savedArticles={savedArticles}
  onStorySelect={setCurrentStoryIndex}
  onMouseDown={handleMouseDown}
  onTouchStart={handleTouchStart}
  onTouchMove={handleTouchMove}
  onTouchEnd={handleTouchEnd}
  onOpenStation={() => setShowStation(true)}
  isArticleSaved={isArticleSaved}
  onSaveArticle={onSaveArticle}
  onRemoveArticle={onRemoveArticle}
/>
          );
        })}

        {/* Source Navigation Dots */}
        <SourceNavigationDots
          sources={newsSources}
          currentIndex={currentSourceIndex}
          onNavigate={(idx) => {
            setCurrentSourceIndex(idx);
            setCurrentStoryIndex(0);
          }}
          onOpenStation={() => setShowStation(true)}
        />
      </div>

      {/* CLOSE CONFIRMATION DIALOG */}
      {showCloseConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '340px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '8px',
              textAlign:'left',
              margin: '0 0 8px 0'
            }}>
              Close Local News?
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              lineHeight: '1.5',
              marginBottom: '20px',
              margin: '0 0 20px 0',
              textAlign:'left',
            }}>
              This will refresh and load new articles next time you open it.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={cancelClose}
                style={{
                  flex: 1,
                  padding: '9px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1f2937',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={confirmClose}
                style={{
                  flex: 1,
                  padding: '9px',
                  background: '#000000',
                  border: 'none',
                  borderRadius: '24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#1f2937'}
                onMouseLeave={(e) => e.target.style.background = '#000000'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Styles */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .item-meta {
          background: transparent;
          border-radius: 0;
          padding: 10px 14px;
          margin-bottom: 10px;
          border: none;
          border-top: 1px solid #d9e0ecff;
        }
          @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>

      {/* STATION MODAL */}
      {showStation && hasGenerated && userLocation && (
        <LocalNewsStation
          userName={userName}
          userLocation={userLocation}
          onClose={() => setShowStation(false)}
        />
      )}
    </div>
  );
};

export default LocalNewsCard;